import {
  Avatar,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { ReactNode, useMemo } from 'react'
import { Divider, Icons, SkeletonText, TextLink } from 'utoniq-ui'

import { Head } from '@/components/layouts'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

import { PaymentMethod } from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { MemberWhetTag } from '@/components/organisms'

import type { Action, State } from '../viewModel'

type UserAreaCellProps = {
  loading: State['loading']
  user: State['user']
  isFollower?: boolean
  membership?: State['membership']
}

type MembershipDataCellProps = {
  loading: State['loading']
  enableFavoriteMembers: State['enableFavoriteMembers']
  membership: State['membership']
  product: State['product']
}

export const ConsoleUserDetailTemplate: Template<State, Action> = ({
  state,
}) => {
  const { routerNames, routerPages } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleUserDetail} />
      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleUsers,
          title: state.user?.fullName ?? state.user?.displayName,
        }}
      >
        <UserAreaCell
          user={state.user}
          loading={state.loading}
          isFollower={state.isFollower}
          membership={state.membership}
        />
        <UserDataCell user={state.user} loading={state.loading} />
        <MembershipDataCell
          enableFavoriteMembers={state.enableFavoriteMembers}
          product={state.product}
          loading={state.loading}
          membership={state.membership}
        />
        <OrderDataCell
          orderCount={state.orderCount}
          user={state.user}
          loading={state.loading}
        />
        <OwnedItemsDataCell
          user={state.user}
          loading={state.loading}
          unusedTicketCount={state.unusedTicketCount}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const UserAreaCell: Cell<UserAreaCellProps> = ({
  user,
  loading,
  isFollower,
  membership,
}) => {
  if (loading) {
    return (
      <CellFrame sx={{ px: 5, pt: 5 }}>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Skeleton variant="circular" sx={{ width: 64, height: 64 }} />
          <Stack spacing={1} flex={1}>
            <SkeletonText variant="large" width="40%" />
            <SkeletonText variant="large" width="25%" />
          </Stack>
        </Stack>
      </CellFrame>
    )
  }
  return (
    <CellFrame sx={{ px: 5, pt: 5 }}>
      <Stack direction="row" alignItems="center" spacing={3}>
        <Avatar
          src={user?.avatar?.smallUri ?? ''}
          sx={{ width: 64, height: 64 }}
        />
        <Stack spacing={1}>
          <Typography variant="h4">
            {user?.fullName ?? user?.displayName}
          </Typography>
          <Stack direction="row" spacing={2}>
            {isFollower && <FollowerTag />}
            {membership && (
              <MemberWhetTag month={membership?.continueMonth ?? 0} />
            )}
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const UserDataCell: Cell<UserAreaCellProps> = ({ user, loading }) => {
  const { t } = useTranslation('consoleUserDetail')

  const userData = useMemo<UserDataListCellProps[]>(
    () => [
      { label: t('email'), content: user?.email },
      { label: t('gender'), content: t(user?.gender ?? '') },
      { label: t('birthday'), content: user?.formattedBirthday },
      { label: t('phoneNumber'), content: user?.formattedPhoneNumber },
      {
        label: t('address'),
        content: user?.address?.fullAddress ?? '',
      },
    ],
    [
      t,
      user?.email,
      user?.gender,
      user?.formattedBirthday,
      user?.formattedPhoneNumber,
      user?.address?.fullAddress,
    ]
  )

  if (loading) {
    return (
      <CellFrame sx={{ pt: 8, px: 5 }}>
        <SkeletonText variant="medium" width="15%" />
        {userData.map((data, index) => (
          <CellFrame key={index}>
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              sx={{ py: 3 }}
            >
              <SkeletonText variant="medium" width="15%" />
              <Stack flex={1}>
                <SkeletonText
                  variant="medium"
                  numberOfLine={data.label === t('address') ? 2 : 1}
                  width={'60%'}
                />
              </Stack>
            </Stack>
            <Divider />
          </CellFrame>
        ))}
      </CellFrame>
    )
  }
  return (
    <CellFrame sx={{ pt: 8, px: 5 }}>
      <Typography variant="h6" color="text.secondary" sx={{ pb: 1 }}>
        {t('userInformation', '顧客情報')}
      </Typography>
      {userData.map((data, index) => (
        <UserDataListCell {...data} key={index} />
      ))}
    </CellFrame>
  )
}

const MembershipDataCell: Cell<MembershipDataCellProps> = ({
  loading,
  enableFavoriteMembers,
  product,
  membership,
}) => {
  const { t } = useTranslation('consoleUserDetail')

  const userData = useMemo<UserDataListCellProps[]>(
    () => [
      { label: t('planName'), content: membership?.subscription?.title },
      // formattedMembershipNumberがあるときだけ表示
      ...(membership?.formattedMembershipNumber
        ? [
            {
              label: t('memberNumber'),
              content: membership.formattedMembershipNumber,
            },
          ]
        : []),
      ...(enableFavoriteMembers
        ? [
            {
              label: t('favoriteMember'),
              content: membership?.favoriteMember?.name || '-',
            },
          ]
        : []),
      { label: t('joinDate'), content: membership?.formattedCreatedAt },
      {
        label: t('continueMonth'),
        content: t('continueMonthValue', { month: membership?.continueMonth }),
      },
      {
        label: t('paymentMethod'),
        content: t(
          `${membership?.paymentMethod ?? PaymentMethod.CreditCard}_${
            product?.plan.interval
          }`
        ),
      },
    ],
    [
      t,
      membership?.subscription?.title,
      membership?.formattedMembershipNumber,
      enableFavoriteMembers,
      membership?.favoriteMember?.name,
      membership?.formattedCreatedAt,
      membership?.continueMonth,
      membership?.paymentMethod,
      product?.plan?.interval,
    ]
  )

  if (!membership || !product) return null

  if (loading) {
    return (
      <CellFrame sx={{ pt: 8, px: 5 }}>
        <SkeletonText variant="medium" width="15%" />
        {userData.map((data, index) => (
          <CellFrame key={index}>
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              sx={{ py: 3 }}
            >
              <SkeletonText variant="medium" width="15%" />
              <Stack flex={1}>
                <SkeletonText
                  variant="medium"
                  numberOfLine={data.label === t('address') ? 2 : 1}
                  lastWidth={data.label === t('address') ? '75%' : '60%'}
                />
              </Stack>
            </Stack>
            <Divider />
          </CellFrame>
        ))}
      </CellFrame>
    )
  }
  return (
    <CellFrame sx={{ pt: 8, px: 5 }}>
      <Typography variant="h6" color="text.secondary" sx={{ pb: 1 }}>
        {t('membershipInformation', 'メンバーシップ情報')}
      </Typography>
      {userData.map((data, index) => (
        <UserDataListCell {...data} key={index} />
      ))}
    </CellFrame>
  )
}

type OrderDataCellProps = {
  user: State['user']
  loading: State['loading']
  orderCount: State['orderCount']
}

const OrderDataCell = ({ user, loading, orderCount }: OrderDataCellProps) => {
  const { t } = useTranslation('consoleUserDetail')
  const { generatePath, routerPages } = useRouter()
  const userData = useMemo<UserDataListCellProps[]>(
    () => [{ label: t('orderCount'), content: orderCount }],
    [orderCount, t]
  )

  if (!loading && !orderCount) return null
  if (loading || !user) {
    return (
      <CellFrame sx={{ pt: 8, px: 5 }}>
        <SkeletonText variant="medium" width="15%" />
        {userData.map((_, index) => (
          <CellFrame key={index}>
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              sx={{ py: 3 }}
            >
              <SkeletonText variant="medium" width="15%" />
              <Stack flex={1}>
                <SkeletonText
                  variant="medium"
                  numberOfLine={1}
                  lastWidth={'60%'}
                />
              </Stack>
            </Stack>
            <Divider />
          </CellFrame>
        ))}
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ pt: 8, px: 5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 1 }}
      >
        <Typography variant="h6" color="text.secondary">
          {t('orderInformation', '注文情報')}
        </Typography>
        <Button
          href={generatePath(routerPages.ConsoleUserOrders, {
            userId: user?.id ?? '#',
          })}
          variant="text"
        >
          {t('orders', '注文一覧')}
        </Button>
      </Stack>

      {userData.map((data, index) => (
        <UserDataListCell {...data} key={index} />
      ))}
    </CellFrame>
  )
}

type UserDataListCellProps = {
  label: string
  content: string | ReactNode
  contentProps?: { href: string }
  icon?: ReactNode
}
const UserDataListCell: Cell<UserDataListCellProps> = ({
  label,
  content,
  contentProps,
  icon,
}) => {
  const { i18n, t } = useTranslation('consoleUserDetail')
  if (!content) {
    return null
  }
  return (
    <CellFrame>
      <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ minWidth: i18n.language === 'en' ? 115 : 65 }}
        >
          {label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          {contentProps?.href ? (
            <TextLink
              component="a"
              variant="body2"
              {...contentProps}
              sx={{ ...styleSets.lineCamp(1) }}
            >
              {content}
            </TextLink>
          ) : (
            <Typography
              variant="body2"
              {...contentProps}
              sx={
                label === t('address')
                  ? { wordBreak: 'break-all' }
                  : { ...styleSets.lineCamp(1) }
              }
            >
              {content}
            </Typography>
          )}
          {icon}

          {label === t('email') && (
            <a href={'mailto:' + content} style={{ lineHeight: 0 }}>
              <Icons.Mail sx={{ ml: 1 }} color="primary" />
            </a>
          )}
        </Stack>
      </Stack>
      <Divider />
    </CellFrame>
  )
}

const FollowerTag = () => {
  const { t } = useTranslation('consoleUserDetail')
  return (
    <Chip
      size="small"
      sx={{ height: 16, borderRadius: '2px', py: 1, bgcolor: 'secondary.main' }}
      label={
        <Typography
          variant="overline"
          display="block"
          sx={{ color: 'white', textTransform: 'unset' }}
        >
          {t('follower')}
        </Typography>
      }
    />
  )
}

type OwnedItemsDataCellProps = {
  user: State['user']
  loading: State['loading']
  unusedTicketCount: State['unusedTicketCount']
}

const OwnedItemsDataCell = ({
  user,
  loading,
  unusedTicketCount,
}: OwnedItemsDataCellProps) => {
  const { t } = useTranslation('consoleUserDetail')
  const { generatePath, routerPages } = useRouter()

  if (loading) {
    return (
      <CellFrame sx={{ pt: 8, px: 5 }}>
        <SkeletonText variant="medium" width="15%" />
        <CellFrame>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 3 }}>
            <SkeletonText variant="medium" width="15%" />
            <Stack flex={1}>
              <SkeletonText
                variant="medium"
                numberOfLine={1}
                lastWidth={'60%'}
              />
            </Stack>
          </Stack>
          <Divider />
        </CellFrame>
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ pt: 8, px: 5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 1 }}
      >
        <Typography variant="h6" color="text.secondary">
          {t('ownedItemsInformation', '所有アイテム一覧')}
        </Typography>
        <Button
          href={generatePath(routerPages.ConsoleUserOwnedItems, {
            userId: user?.id ?? '#',
          })}
          variant="text"
        >
          {t('toItemsList', '所有アイテム一覧')}
        </Button>
      </Stack>

      <UserDataListCell
        label={t('unusedTicket', '未使用チケット')}
        content={unusedTicketCount}
      />
    </CellFrame>
  )
}
